import { Group, Loader } from '@mantine/core';
import type { CatalogRule, Filter } from '@repo/api-codegen';
import {
	getRunSqlExcludedFiltersQueryKey,
	getRunSqlIncludedFiltersQueryKey,
	useGetExcludedFilters,
	useGetIncludedFilters,
	useGetRunSqlExcludedFilters,
	useGetRunSqlIncludedFilters,
	useSetRunSqlExcludedFilters,
	useSetRunSqlIncludedFilters,
} from '@repo/api-codegen';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { queryClient, useDataQualityAccess } from '../../../api';
import {
	DEFAULT_FILTER_OPTIONS,
	DEFAULT_FILTER_OPTIONS_WITH_DQS,
	FILTER_OPTIONS_DIVIDER,
} from '../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../Filter/constants';
import { GovernanceFilters } from '../GovernanceFilters/GovernanceFilters';

interface IAIPersonasRunSqlFiltersInlineProps {
	personaId: string;
}

export const AIPersonasRunSqlFiltersInlineInternal = observer(
	({ personaId }: IAIPersonasRunSqlFiltersInlineProps) => {
		const { data: includedFilters, isLoading: isLoadingIncluded } =
			useGetRunSqlIncludedFilters(
				{
					pathParams: {
						personaId,
					},
				},
				{
					enabled: !!personaId,
				}
			);
		const { mutateAsync: updateIncludedFilters } = useSetRunSqlIncludedFilters({
			onSuccess: (_, variables) => {
				// optimistic update
				queryClient.setQueryData(
					getRunSqlIncludedFiltersQueryKey({
						pathParams: {
							personaId,
						},
					}),
					variables.body
				);
			},
		});
		const { data: excludedFilters, isLoading: isLoadingExcluded } =
			useGetRunSqlExcludedFilters(
				{
					pathParams: {
						personaId,
					},
				},
				{
					enabled: !!personaId,
				}
			);
		const { mutateAsync: updateExcludedFilters } = useSetRunSqlExcludedFilters({
			onSuccess: (_, variables) => {
				// optimistic update
				queryClient.setQueryData(
					getRunSqlExcludedFiltersQueryKey({
						pathParams: {
							personaId,
						},
					}),
					variables.body
				);
			},
		});

		const handleOnIncludedFiltersChange = useCallback(
			async (catalogRule: CatalogRule) => {
				await updateIncludedFilters({
					body: catalogRule.operands,
					pathParams: { personaId },
				});
			},
			[updateIncludedFilters, personaId]
		);

		const handleOnExcludedFiltersChange = useCallback(
			async (catalogRule: CatalogRule) => {
				await updateExcludedFilters({
					body: catalogRule.operands,
					pathParams: { personaId },
				});
			},
			[updateExcludedFilters, personaId]
		);

		const {
			data: personaIncludedFilters,
			isLoading: isLoadingPersonaIncluded,
		} = useGetIncludedFilters({
			pathParams: {
				personaId,
			},
		});
		const {
			data: personaExcludedFilters,
			isLoading: isLoadingPersonaExcluded,
		} = useGetExcludedFilters({
			pathParams: {
				personaId,
			},
		});

		// This must match the backend implementation
		const filter: Filter = useMemo(() => {
			const operands: Filter[] = [];

			if (includedFilters && includedFilters.length > 0) {
				operands.push({
					operator: 'or',
					operands: includedFilters,
				});
			}

			if (excludedFilters && excludedFilters.length > 0) {
				operands.push({
					operator: 'not',
					operands: excludedFilters,
				});
			}

			if (personaIncludedFilters && personaIncludedFilters.length > 0) {
				operands.push({
					operator: 'or',
					operands: personaIncludedFilters,
				});
			}

			if (personaExcludedFilters && personaExcludedFilters.length > 0) {
				operands.push({
					operator: 'not',
					operands: personaExcludedFilters,
				});
			}

			return {
				operator: 'and',
				operands: operands,
			};
		}, [
			includedFilters,
			excludedFilters,
			personaIncludedFilters,
			personaExcludedFilters,
		]);

		const dqsEnabled = useDataQualityAccess();
		const filterOptions = dqsEnabled
			? DEFAULT_FILTER_OPTIONS_WITH_DQS
			: DEFAULT_FILTER_OPTIONS;

		// Top level operator is always or in the backend
		// It's added here just for typing, but when passing to the backend, we strip out the operator and pass only the operands
		const includedCatalogFilters: CatalogRule = useMemo(
			() => ({ operator: 'or', operands: includedFilters ?? [] }),
			[includedFilters]
		);
		const excludedCatalogFilters: CatalogRule = useMemo(
			() => ({ operator: 'or', operands: excludedFilters ?? [] }),
			[excludedFilters]
		);

		// Show loader while data is being fetched
		if (
			isLoadingIncluded ||
			isLoadingExcluded ||
			isLoadingPersonaIncluded ||
			isLoadingPersonaExcluded
		) {
			return (
				<Group position="center">
					<Loader />
				</Group>
			);
		}

		return (
			<GovernanceFilters
				previewFilter={filter}
				emptyStateMessage="Currently, the AI assistant can access all resources in your workspace with the SQL tool (subject to permissions of the user asking the question and Persona level permissions). To limit the resources, add Run SQL filters."
				includedFilters={includedCatalogFilters}
				excludedFilters={excludedCatalogFilters}
				onIncludedFiltersChange={handleOnIncludedFiltersChange}
				onExcludedFiltersChange={handleOnExcludedFiltersChange}
				filterOptions={filterOptions.map((option) =>
					option === FILTER_OPTIONS_DIVIDER
						? option
						: FILTER_OPTIONS_CONFIG[option]
				)}
				disableTopLevelOperatorToggle
			/>
		);
	}
);

// Wrapper component that forces remount when data loads
export function AIPersonasRunSqlFiltersInline({
	personaId,
}: IAIPersonasRunSqlFiltersInlineProps) {
	const { data: includedFilters, isLoading: isLoadingIncluded } =
		useGetRunSqlIncludedFilters(
			{
				pathParams: {
					personaId,
				},
			},
			{
				enabled: !!personaId,
			}
		);
	const { data: excludedFilters, isLoading: isLoadingExcluded } =
		useGetRunSqlExcludedFilters(
			{
				pathParams: {
					personaId,
				},
			},
			{
				enabled: !!personaId,
			}
		);

	const { data: personaIncludedFilters, isLoading: isLoadingPersonaIncluded } =
		useGetIncludedFilters({
			pathParams: {
				personaId,
			},
		});
	const { data: personaExcludedFilters, isLoading: isLoadingPersonaExcluded } =
		useGetExcludedFilters({
			pathParams: {
				personaId,
			},
		});

	// Show loader while data is being fetched
	if (
		isLoadingIncluded ||
		isLoadingExcluded ||
		isLoadingPersonaIncluded ||
		isLoadingPersonaExcluded
	) {
		return (
			<Group position="center">
				<Loader />
			</Group>
		);
	}

	// Use a key to force remount when data loads
	// This ensures the store is initialized with the correct data
	const hasData =
		includedFilters !== undefined &&
		excludedFilters !== undefined &&
		personaIncludedFilters !== undefined &&
		personaExcludedFilters !== undefined;
	const key = hasData
		? `filters-loaded-${personaId}`
		: `filters-loading-${personaId}`;

	return (
		<AIPersonasRunSqlFiltersInlineInternal key={key} personaId={personaId} />
	);
}
